import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { ChevronDown, CalendarCheck, LayoutDashboard, CalendarPlus, Menu, X, Mail } from 'lucide-react';
import { ROUTES } from '../../routes/routePaths';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const colors = {
    primary: '#61257B',
    primaryDark: '#451759',
    secondary: '#C1348B',
    secondaryLight: '#FFD97C',
    accentDark: '#EA8406',
};

interface NavItem {
    id: string;
    label: string;
    icon: React.ReactNode;
    color: string;
    links: { href: string; label: string; desc?: string }[];
}

const navItems: NavItem[] = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        icon: <LayoutDashboard size={16} />,
        color: '#8b5cf6', // Violeta / Morado
        links: [{ href: '/midashboard', label: 'Mi Dashboard', desc: 'Resumen de tu actividad' }],
    },
    {
        id: 'correspondencia',
        label: 'Correspondencia',
        icon: <Mail size={16} />,
        color: '#2dd4bf', // Turquesa / Teal
        links: [{ href: '/mi-correspondencia', label: 'Mi Correspondencia', desc: 'Paquetes, fotos y PIN de retiro' }],
    },
    {
        id: 'miscitas',
        label: 'Reservas',
        icon: <CalendarCheck size={16} />,
        color:'var(--ad-link, #2854c5)',
        links: [
            { href: ROUTES.misCitas, label: 'Mis Reservas', desc: 'Calendario y lista de reservas' },
            { href: ROUTES.misCotizaciones, label: 'Mis cotizaciones', desc: 'Descarga el PDF y solicita tu reserva' },
            { href: ROUTES.solicitudesReserva, label: 'Solicitudes de reserva', desc: 'Pagos y solicitudes pendientes' },
            { href: ROUTES.misFacturas, label: 'Mis Facturas', desc: 'Consulta y descarga tus facturas' },
        ],
    },
    {
        id: 'reservaya',
        label: 'Reserva Ya',
        icon: <CalendarPlus size={16} />,
        color: '#f59e0b', // Ámbar / Dorado
        links: [{ href: '/reservaya', label: 'Reserva Ya', desc: 'Haz tu reserva ahora' }],
    },
    {
        id: 'parati',
        label: 'Para Ti',
        icon: <CalendarPlus size={16} />,
        color: '#c084fc', // Lavanda / Púrpura claro
        links: [{ href: '/parati', label: 'Para Ti', desc: 'Servicios personalizados' }],
    },
];

const Navbar: React.FC = () => {
    const { user } = useAuth();
    const { isDark } = useTheme();
    const location = useLocation();
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);

    const itemsVisibles = useMemo(() => {
        return navItems.filter(item => {
            if (item.id === 'correspondencia') {
                if (!user) return false;
                const isStaffOrAdmin = user.perfil === 'personal' || user.role === 'Administrador' || user.role === 'personal';
                return isStaffOrAdmin || Boolean(user.es_domiciliado) || Boolean(user.tiene_correspondencia_habilitada) || user.role?.toLowerCase() === 'domiciliado';
            }
            return true;
        });
    }, [user]);

    const isItemActive = (item: NavItem) => {
        if (openDropdown === item.id) return true;
        return item.links.some(l => location.pathname === l.href || (l.href !== '/' && location.pathname.startsWith(l.href)));
    };

    const toggleDropdown = (id: string) => setOpenDropdown(openDropdown === id ? null : id);
    const toggleMobileItem = (id: string) => setMobileExpanded(mobileExpanded === id ? null : id);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('[data-dropdown-trigger]') && !target.closest('[data-dropdown-content]')) {
                setOpenDropdown(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 4);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Cerrar menú móvil al hacer resize a desktop
    useEffect(() => {
        const handleResize = () => { if (window.innerWidth >= 640) setMobileOpen(false); };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
                .nav-trigger {
                    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1) !important;
                }
                .nav-trigger:hover {
                    transform: translateY(-1px);
                }
                .nav-icon-box {
                    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1) !important;
                }
                .nav-text {
                    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1) !important;
                }
                .nav-chevron {
                    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1) !important;
                }
                .nav-link-item { transition: all 0.15s; }
                .nav-link-item:hover { transform: translateX(3px); }
                .nav-hamburger { display: none; }
                .nav-desktop-items { display: flex; }
                @media (max-width: 639px) {
                    .nav-hamburger { display: flex !important; }
                    .nav-desktop-items { display: none !important; }
                    .nav-brand-bar { display: none !important; }
                }
            `}</style>

            <nav style={{
                fontFamily: "'DM Sans', sans-serif",
                background: isDark ? 'rgba(15, 8, 29, 0.94)' : '#fff',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderBottom: isDark 
                    ? (scrolled || mobileOpen ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid transparent') 
                    : (scrolled || mobileOpen ? '1.5px solid #f1f5f9' : '1.5px solid transparent'),
                boxShadow: scrolled 
                    ? (isDark ? '0 8px 24px rgba(0,0,0,0.6)' : '0 2px 16px rgba(0,0,0,0.05)') 
                    : 'none',
                transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
                position: 'sticky', top: '64px', zIndex: 40,
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(16px,3vw,32px)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', height: '52px', gap: '4px' }}>

                        {/* Barra de colores — solo desktop */}
                        <div className="nav-brand-bar" style={{ display: 'flex', gap: '3px', marginRight: '16px' }}>
                            {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c, i) => (
                                <div key={i} style={{ width: i === 0 ? '14px' : '5px', height: '3px', borderRadius: '3px', background: c }} />
                            ))}
                        </div>

                        {/* ── Desktop items ── */}
                        <div className="nav-desktop-items" style={{ alignItems: 'center', gap: '4px' }}>
                            {itemsVisibles.map(item => {
                                const active = isItemActive(item);
                                return (
                                    <div key={item.id} style={{ position: 'relative' }}>
                                        <button
                                            data-dropdown-trigger={item.id}
                                            onClick={() => toggleDropdown(item.id)}
                                            className="nav-trigger"
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '7px',
                                                padding: '7px 14px', borderRadius: '10px',
                                                border: active 
                                                    ? `1.5px solid ${item.color}60` 
                                                    : '1.5px solid transparent',
                                                background: active 
                                                    ? (isDark ? `${item.color}25` : `${item.color}10`) 
                                                    : 'transparent',
                                                fontSize: '0.83rem', fontWeight: '700',
                                                color: active 
                                                    ? item.color 
                                                    : (isDark ? '#ffffff' : '#334155'),
                                                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                            }}
                                            onMouseEnter={e => {
                                                if (!active) {
                                                    e.currentTarget.style.background = isDark ? `${item.color}18` : `${item.color}0a`;
                                                    e.currentTarget.style.color = item.color;
                                                    e.currentTarget.style.border = `1.5px solid ${item.color}40`;
                                                    const iconBox = e.currentTarget.querySelector('.nav-icon-box') as HTMLElement | null;
                                                    if (iconBox) {
                                                        iconBox.style.background = item.color;
                                                        iconBox.style.color = '#ffffff';
                                                        iconBox.style.boxShadow = `0 2px 10px ${item.color}50`;
                                                        iconBox.style.borderColor = item.color;
                                                    }
                                                    const chev = e.currentTarget.querySelector('.nav-chevron') as HTMLElement | null;
                                                    if (chev) {
                                                        chev.style.color = item.color;
                                                    }
                                                }
                                            }}
                                            onMouseLeave={e => {
                                                if (!active) {
                                                    e.currentTarget.style.background = 'transparent';
                                                    e.currentTarget.style.color = isDark ? '#ffffff' : '#334155';
                                                    e.currentTarget.style.border = '1.5px solid transparent';
                                                    const iconBox = e.currentTarget.querySelector('.nav-icon-box') as HTMLElement | null;
                                                    if (iconBox) {
                                                        iconBox.style.background = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)';
                                                        iconBox.style.color = isDark ? '#ffffff' : '#475569';
                                                        iconBox.style.boxShadow = 'none';
                                                        iconBox.style.borderColor = isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)';
                                                    }
                                                    const chev = e.currentTarget.querySelector('.nav-chevron') as HTMLElement | null;
                                                    if (chev) {
                                                        chev.style.color = isDark ? 'rgba(255, 255, 255, 0.7)' : '#94a3b8';
                                                    }
                                                }
                                            }}
                                        >
                                            <span 
                                                className="nav-icon-box"
                                                style={{
                                                    width: '26px', height: '26px', borderRadius: '7px',
                                                    background: active ? item.color : (isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'),
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: active ? '#ffffff' : (isDark ? '#ffffff' : '#475569'),
                                                    border: active ? `1px solid ${item.color}` : (isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.08)'),
                                                    boxShadow: active ? `0 2px 10px ${item.color}50` : 'none',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {item.icon}
                                            </span>
                                            <span className="nav-text">
                                                {item.label}
                                            </span>
                                            <ChevronDown 
                                                size={13} 
                                                className="nav-chevron"
                                                style={{
                                                    color: active ? item.color : (isDark ? 'rgba(255, 255, 255, 0.7)' : '#94a3b8'),
                                                    transform: openDropdown === item.id ? 'rotate(180deg)' : 'rotate(0deg)',
                                                }} 
                                            />
                                        </button>

                                        <Transition
                                            show={openDropdown === item.id}
                                            enter="transition ease-out duration-200"
                                            enterFrom="opacity-0 translate-y-1"
                                            enterTo="opacity-100 translate-y-0"
                                            leave="transition ease-in duration-150"
                                            leaveFrom="opacity-100 translate-y-0"
                                            leaveTo="opacity-0 translate-y-1"
                                        >
                                            <div data-dropdown-content={item.id} style={{
                                                position: 'absolute', top: 'calc(100% + 8px)', left: 0,
                                                minWidth: '220px', 
                                                background: isDark ? '#16092b' : '#fff', 
                                                borderRadius: '16px',
                                                border: isDark ? '1.5px solid rgba(193, 52, 139, 0.35)' : '1.5px solid #f1f5f9',
                                                boxShadow: isDark 
                                                    ? `0 16px 48px rgba(0,0,0,0.7), 0 0 25px rgba(193, 52, 139, 0.25)` 
                                                    : `0 16px 48px rgba(0,0,0,0.09), 0 4px 16px rgba(0,0,0,0.05)`,
                                                overflow: 'hidden', zIndex: 100, fontFamily: "'DM Sans', sans-serif",
                                            }}>
                                                <div style={{ height: '3px', background: `linear-gradient(90deg,${item.color},${item.color}40)` }} />
                                                <div style={{ padding: '8px' }}>
                                                    {item.links.map(link => (
                                                        <a key={link.href} href={link.href} className="nav-link-item"
                                                            style={{ 
                                                                display: 'flex', alignItems: 'center', gap: '10px', 
                                                                padding: '10px 12px', borderRadius: '10px', textDecoration: 'none', 
                                                                color: isDark ? '#cbd5e1' : '#374151' 
                                                            }}
                                                            onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(193, 52, 139, 0.15)' : `${item.color}08`; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                                        >
                                                            <div style={{ 
                                                                width: '30px', height: '30px', borderRadius: '9px', flexShrink: 0, 
                                                                background: `${item.color}15`, border: `1.5px solid ${item.color}25`, 
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.color 
                                                            }}>
                                                                {item.icon}
                                                            </div>
                                                            <div>
                                                                <div style={{ fontSize: '0.83rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.2' }}>{link.label}</div>
                                                                {link.desc && <div style={{ fontSize: '0.7rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '500', marginTop: '2px' }}>{link.desc}</div>}
                                                            </div>
                                                            <div style={{ marginLeft: 'auto', fontSize: '0.75rem', color: `${item.color}80` }}>→</div>
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        </Transition>
                                    </div>
                                );
                            })}
                        </div>

                        {/* ── Hamburguesa — solo móvil ── */}
                        <div className="nav-hamburger" style={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            {/* Barra colores — izquierda */}
                            <div style={{ display: 'flex', gap: '4px' }}>
                                {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c, i) => (
                                    <div key={i} style={{ width: i === 0 ? '14px' : '5px', height: '3px', borderRadius: '3px', background: c }} />
                                ))}
                            </div>
                            {/* Botón hamburguesa — derecha */}
                            <button
                                onClick={() => { setMobileOpen(o => !o); setMobileExpanded(null); }}
                                style={{
                                    width: '36px', height: '36px', borderRadius: '10px',
                                    border: isDark ? '1.5px solid rgba(193, 52, 139, 0.3)' : '1.5px solid #f1f5f9', 
                                    background: isDark ? '#1b0f30' : '#f8fafc',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', transition: 'all .2s',
                                }}
                            >
                                {mobileOpen
                                    ? <X size={16} color={isDark ? '#f8fafc' : '#374151'} />
                                    : <Menu size={16} color={isDark ? '#f8fafc' : '#374151'} />
                                }
                            </button>
                        </div>

                    </div>
                </div>

                {/* ── Menú móvil desplegable ── */}
                <Transition
                    show={mobileOpen}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 -translate-y-2"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 -translate-y-2"
                >
                    <div style={{
                        borderTop: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #f1f5f9',
                        background: isDark ? '#110820' : '#fff',
                        padding: '8px 16px 12px',
                    }}>
                        {itemsVisibles.map(item => {
                            const active = isItemActive(item) || mobileExpanded === item.id;
                            return (
                                <div key={item.id}>
                                    {/* Fila item */}
                                    <button
                                        onClick={() => toggleMobileItem(item.id)}
                                        style={{
                                            width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                                            padding: '10px 12px', borderRadius: '12px', 
                                            border: active ? `1.5px solid ${item.color}50` : '1.5px solid transparent',
                                            background: active ? (isDark ? `${item.color}25` : `${item.color}10`) : 'transparent',
                                            cursor: 'pointer', fontFamily: "'DM Sans',sans-serif",
                                            transition: 'all 0.35s ease-in-out',
                                        }}
                                    >
                                        <span style={{
                                            width: '28px', height: '28px', borderRadius: '8px',
                                            background: active ? item.color : (isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'),
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: active ? '#ffffff' : (isDark ? '#ffffff' : '#475569'),
                                            border: active ? `1px solid ${item.color}` : (isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.08)'),
                                            boxShadow: active ? `0 2px 10px ${item.color}50` : 'none',
                                            transition: 'all 0.35s ease-in-out', flexShrink: 0,
                                        }}>
                                            {item.icon}
                                        </span>
                                        <span style={{ 
                                            fontSize: '.88rem', fontWeight: '700', 
                                            color: active ? item.color : (isDark ? '#ffffff' : '#374151'), 
                                            flex: 1, textAlign: 'left',
                                            transition: 'color 0.35s ease-in-out',
                                        }}>
                                            {item.label}
                                        </span>
                                        <ChevronDown size={13} color={active ? item.color : (isDark ? 'rgba(255, 255, 255, 0.7)' : '#94a3b8')} style={{ transition: 'all 0.35s ease-in-out, transform .25s', transform: mobileExpanded === item.id ? 'rotate(180deg)' : 'rotate(0)' }} />
                                    </button>

                                    {/* Sub-links expandibles */}
                                    {mobileExpanded === item.id && (
                                        <div style={{ paddingLeft: '50px', paddingBottom: '4px' }}>
                                            {item.links.map(link => (
                                                <a key={link.href} href={link.href} 
                                                    style={{ 
                                                        display: 'flex', alignItems: 'center', gap: '8px', 
                                                        padding: '8px 12px', borderRadius: '10px', textDecoration: 'none', 
                                                        color: isDark ? '#cbd5e1' : '#374151', transition: 'background .15s' 
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(193, 52, 139, 0.15)' : `${item.color}08`; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                                >
                                                    <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                                                    <div>
                                                        <div style={{ fontSize: '.83rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a' }}>{link.label}</div>
                                                        {link.desc && <div style={{ fontSize: '.7rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '500' }}>{link.desc}</div>}
                                                    </div>
                                                    <div style={{ marginLeft: 'auto', fontSize: '.75rem', color: `${item.color}80` }}>→</div>
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Transition>
            </nav>
        </>
    );
};

export default Navbar;

